import React, { useState } from "react";
import { Link } from "react-router-dom";
import emailjs from "emailjs-com";
import { toast } from "react-toastify";
import {
  ArrowUpRight, ShoppingBag, Layers, Search, TrendingUp, RefreshCw, Sparkles,
  Globe2, Smartphone, Gauge, ChevronDown, Terminal, Server, Cloud,
} from "lucide-react";
import { JHALA_GLOBAL_CSS, TopStrip, Header, Footer, Reveal, TagChip } from "components/jhala/Layout.js";

import pinStoreFrame from "images/companyWorkImage/pinstore/frame.png";
import truFrame from "images/companyWorkImage/truHair&Skin/frame.png";
import natureFrame from "images/companyWorkImage/natureMasons/frame.png";
import muffFrame from "images/companyWorkImage/mufftech/frame.png";
import jewelryShowcase1 from "images/jwellary.png";
import jewelryShowcase2 from "images/jwellary1.png";
import fashionShowcase1 from "images/Fashion & Apparel.png";
import fashionShowcase2 from "images/Fashion & Apparel1.png";
import beautyShowcase1 from "images/Beauty & Skincare.png";
import beautyShowcase2 from "images/Beauty & Skincare1.png";
import ecoShowcase1 from "images/Eco & Home Goods.png";
import ecoShowcase2 from "images/Eco & Home Goods1.png";
import healthShowcase1 from "images/Health & Wellness.png";
import healthShowcase2 from "images/Health & Wellness1.png";

/*
  Content source: JhalaTech Ecommerce Landing Page Handoff (dev/content brief).
  Base page flow per handoff: Header > Hero > Services > Market-Ready Foundations >
  AI Automation > Supporting Full-Stack Services > Process > FAQs > Audit Form > Footer.

  Added back on request: Industries marquee (real showcase images) and Work
  section (real project screenshots) — placed after Core Services and before
  Market-Ready Foundations, so the new PDF-driven sections stay in their
  specified order.

  Testimonials remain removed per handoff direction: add client results/
  testimonials only once verified content is supplied.

  Header/Footer come from components/jhala/Layout.js.
*/

const HERO_CAPABILITIES = [
  "Shopify Development",
  "SEO-Optimized Structure",
  "CRO-Focused UX",
  "Mobile-First Build",
  "Custom AI Automation",
];

// Figures drawn directly from real shipped projects (see Work.js project
// ratings/results) — not placeholder numbers.
const IMPACT_STATS = [
  { value: "50+", label: "real Shopify & ecommerce stores shipped" },
  { value: "4.7★", label: "average client rating across projects" },
  { value: "70%", label: "peak lift in conversions & sign-ups" },
  { value: "90%", label: "faster page loads on rebuilt stores" },
];

const CORE_SERVICES = [
  {
    icon: ShoppingBag,
    title: "Shopify Store Development",
    desc: "Launch a fast, responsive and easy-to-manage Shopify or Shopify Plus store built around your products, customers and growth goals.",
  },
  {
    icon: Layers,
    title: "Ecommerce Website Development",
    desc: "Build a market-ready ecommerce experience with clear product discovery, mobile-first journeys and reliable checkout paths.",
  },
  {
    icon: RefreshCw,
    title: "Ecommerce Store Redesign",
    desc: "Turn an outdated or underperforming store into a clearer, faster and more conversion-focused shopping experience.",
  },
  {
    icon: Globe2,
    title: "Ecommerce Platform Migration",
    desc: "Move your store to a better-fit platform with a planned approach to products, content, URLs, integrations and launch.",
  },
  {
    icon: Search,
    title: "SEO & CRO Optimization",
    desc: "Improve how customers find, understand and buy from your store through search-ready structure and conversion-focused journeys.",
  },
  {
    icon: Sparkles,
    title: "Custom AI Automation for Ecommerce",
    desc: "Reduce repetitive ecommerce work with custom AI automation designed around your workflows, tools and business rules.",
    badge: true,
  },
];

const INDUSTRIES = [
  { tag: "#JEWELRY", title: "Jewelry & Accessories", images: [jewelryShowcase1, jewelryShowcase2] },
  { tag: "#FASHION", title: "Fashion & Apparel", images: [fashionShowcase1, fashionShowcase2] },
  { tag: "#BEAUTY", title: "Beauty & Skincare", images: [beautyShowcase1, beautyShowcase2] },
  { tag: "#ECO-HOME", title: "Eco & Home Goods", images: [ecoShowcase1, ecoShowcase2] },
  { tag: "#WELLNESS", title: "Health & Wellness", images: [healthShowcase1, healthShowcase2] },
];

const INDUSTRY_STRIP = INDUSTRIES.flatMap((ind) =>
  ind.images.map((src) => ({ src, tag: ind.tag, title: ind.title }))
);

const PROJECTS = [
  { name: "The Pins Store", cat: "Fashion Accessories · Shopify", hash: "a4f9c2", result: "50% faster page load times", image: pinStoreFrame },
  { name: "Tru Hair and Skin", cat: "Beauty & Skincare · Shopify", hash: "e21b7d", result: "60% increase in subscription sign-ups", image: truFrame },
  { name: "The Nature Masons", cat: "Eco-Friendly Products · Shopify", hash: "9c3f10", result: "50% increase in search-to-purchase conversion", image: natureFrame },
  { name: "Mufftech", cat: "B2B & B2C · Shopify", hash: "6d0aa5", result: "Scaled to handle 3x order volume", image: muffFrame },
];

const FOUNDATIONS = [
  { icon: Search, title: "SEO-Optimized Foundations", desc: "Clear page hierarchy, internal links, crawlable content, metadata and technical SEO foundations from the start." },
  { icon: TrendingUp, title: "CRO-Focused Shopping Journeys", desc: "Products easier to discover, understand and buy with clear navigation, product communication and calls to action." },
  { icon: Sparkles, title: "Search and AI-Discovery Ready", desc: "Semantic HTML, structured content and useful FAQs so search engines and AI-assisted discovery systems can understand the offer." },
  { icon: Smartphone, title: "Mobile-First Experience", desc: "Designed and tested around how customers browse, compare and purchase on smaller screens." },
  { icon: Gauge, title: "Performance and Tracking", desc: "Built for speed, with analytics and conversion events connected to measure what happens after launch." },
];

const AI_STEPS = [
  { step: "01", label: "Identify the repetitive workflow" },
  { step: "02", label: "Define the inputs, outputs and approval points" },
  { step: "03", label: "Build and connect the automation" },
  { step: "04", label: "Test, document and hand over the workflow" },
];

const FULLSTACK_SUPPORT = [
  { icon: Layers, label: "React and Next.js development" },
  { icon: Server, label: "Node.js backend services" },
  { icon: Terminal, label: "API development and integrations" },
  { icon: Cloud, label: "DevOps and deployment support" },
];

const PROCESS = [
  { step: "01", title: "Store Audit and Discovery", desc: "Review the current store or project, business goals, customers and technical requirements." },
  { step: "02", title: "Ecommerce, SEO and CRO Strategy", desc: "Define the store structure, user journeys, search requirements, conversion priorities and project roadmap." },
  { step: "03", title: "UX and Design", desc: "Create the page structure and visual experience for browsing, product discovery and buying." },
  { step: "04", title: "Development and Integrations", desc: "Build the approved store and connect the required ecommerce systems." },
  { step: "05", title: "Testing and Launch", desc: "Test mobile behaviour, key shopping journeys, SEO foundations, tracking and technical quality before launch." },
];

const FAQS = [
  { q: "What is JhalaTech's main ecommerce focus?", a: "Shopify and Shopify Plus development are the primary ecommerce focus, including new stores, redesigns, improvements and migrations." },
  { q: "Are SEO and CRO included in the store build?", a: "SEO and CRO foundations are planned into the structure, design and development. The final scope depends on the store, content and growth requirements." },
  { q: "Can JhalaTech migrate an existing ecommerce store?", a: "Yes. Migration work can include planning, content and data transfer, redirect requirements, integrations, testing and launch support." },
  { q: "What type of ecommerce AI automation do you build?", a: "AI automation is custom to the business workflow. We first define the process, tools, rules and approval points before recommending or building the automation." },
  { q: "Do you also provide full-stack development?", a: "Yes. React, Next.js, Node.js, API and DevOps services are available when they support an ecommerce requirement or another custom project." },
  { q: "How do we get started?", a: "Start with a free store audit and share your current website, priorities and project goals." },
];

const NEED_OPTIONS = [
  "New Shopify store",
  "Ecommerce website development",
  "Ecommerce store redesign",
  "Platform migration",
  "SEO or CRO optimization",
  "Custom ecommerce AI automation",
  "Supporting full-stack development",
  "Not sure yet",
];

function IndustryMarquee({ items }) {
  const track = [...items, ...items];
  return (
    <div className="industry-marquee" style={{ overflow: "hidden", margin: "0 -24px", padding: "10px 0 20px" }}>
      <div className="industry-marquee-track" style={{ display: "flex", width: "max-content", gap: 22, animation: "marquee 38s linear infinite" }}>
        {track.map((item, i) => (
          <div
            key={i}
            style={{
              position: "relative", flexShrink: 0, width: 210, borderRadius: 14, overflow: "hidden",
              boxShadow: "0 20px 44px -18px rgba(10,15,30,0.3)",
              marginTop: i % 2 === 0 ? 0 : 30,
            }}
          >
            <img src={item.src} alt={item.title} style={{ width: "100%", height: 400, objectFit: "cover", objectPosition: "top center", display: "block" }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 55%, rgba(10,15,30,0.9) 100%)" }} />
            <div style={{ position: "absolute", bottom: 14, left: 14, right: 14 }}>
              <TagChip tone="signal">{item.tag}</TagChip>
              <div style={{ color: "#F6F4EE", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: 14, marginTop: 8 }}>{item.title}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FAQItem({ q, a, isOpen, onToggle }) {
  return (
    <div style={{ borderBottom: "1px solid #DCD8CC" }}>
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        style={{
          width: "100%", background: "none", border: "none", cursor: "pointer",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "20px 4px", textAlign: "left", gap: 16,
        }}
      >
        <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15.5, fontWeight: 600, margin: 0, color: "#0A0F1E" }}>{q}</h3>
        <ChevronDown size={18} style={{ color: "#4CE0B3", flexShrink: 0, transition: "transform 0.25s ease", transform: isOpen ? "rotate(180deg)" : "none" }} />
      </button>
      <div style={{ maxHeight: isOpen ? 200 : 0, overflow: "hidden", transition: "max-height 0.3s ease" }}>
        <p style={{ color: "#4A5468", fontSize: 14, lineHeight: 1.7, margin: "0 4px 20px" }}>{a}</p>
      </div>
    </div>
  );
}

function AuditForm() {
  const [need, setNeed] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.target;

    // The shared EmailJS template only renders {{subject}} and {{message}} (the
    // fields the Contact form uses) — so every audit-form field is folded into
    // those two here to guarantee it actually shows up in the email body,
    // regardless of which template variables the dashboard template wires up.
    form.subject.value = "New Free Store Audit Request";
    form.message.value = [
      `Company: ${form.company.value || "-"}`,
      `Website: ${form.website.value || "-"}`,
      `What they need: ${form.need.value || "-"}`,
      "",
      "Project details:",
      form.details.value || "-",
    ].join("\n");

    const SERVICE_ID = "service_xzlcvo1";
    const TEMPLATE_ID = "template_caupis8";
    const USER_ID = "XlRMBTadMyxn2irk2";

    emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, form, USER_ID)
      .then(() => {
        toast.success("Your store audit request has been sent. We'll get back to you shortly.");
      }, () => {
        toast.error("Failed to send your request. Please try again.");
      });

    form.reset();
    setNeed("");
  };

  const inputStyle = {
    width: "100%", padding: "11px 14px", borderRadius: 6, border: "1px solid #DCD8CC",
    background: "#FFFFFF", fontSize: 14, fontFamily: "'Inter', sans-serif", color: "#0A0F1E",
    outline: "none",
  };
  const labelStyle = { display: "block", fontSize: 13, fontWeight: 600, color: "#0A0F1E", marginBottom: 6 };

  return (
    <form onSubmit={handleSubmit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }} className="audit-form-grid">
      <input type="hidden" name="to_email" value="itadmin@jhalatech.com" />
      <input type="hidden" name="subject" />
      <textarea style={{ display: "none" }} name="message" />
      <div>
        <label htmlFor="af-name" style={labelStyle}>Full Name</label>
        <input id="af-name" name="name" type="text" placeholder="Enter your name" required style={inputStyle} />
      </div>
      <div>
        <label htmlFor="af-email" style={labelStyle}>Work Email</label>
        <input id="af-email" name="email" type="email" placeholder="Enter your work email" required style={inputStyle} />
      </div>
      <div>
        <label htmlFor="af-company" style={labelStyle}>Company</label>
        <input id="af-company" name="company" type="text" placeholder="Enter your company name" style={inputStyle} />
      </div>
      <div>
        <label htmlFor="af-url" style={labelStyle}>Website URL</label>
        <input id="af-url" name="website" type="url" placeholder="Enter your website URL" style={inputStyle} />
      </div>
      <div style={{ gridColumn: "1 / -1" }}>
        <label htmlFor="af-need" style={labelStyle}>What do you need?</label>
        <select id="af-need" name="need" value={need} onChange={(e) => setNeed(e.target.value)} required style={{ ...inputStyle, appearance: "auto" }}>
          <option value="" disabled>Select an option</option>
          {NEED_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
      <div style={{ gridColumn: "1 / -1" }}>
        <label htmlFor="af-details" style={labelStyle}>Project Details</label>
        <textarea id="af-details" name="details" rows={4} placeholder="Share your current challenges, requirements and goals" style={{ ...inputStyle, resize: "vertical" }} />
      </div>
      <div style={{ gridColumn: "1 / -1" }}>
        <button type="submit" style={{ background: "#FF5C39", color: "#0A0F1E", border: "none", padding: "13px 26px", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: "pointer", width: "100%" }}>
          Get My Free Store Audit
        </button>
        <p style={{ fontSize: 12.5, color: "#8B93A7", marginTop: 12, textAlign: "center" }}>
          No generic sales presentation. Start with a practical review and clear next steps.
        </p>
      </div>
    </form>
  );
}

export default function MainLandingPage() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: "#F6F4EE", color: "#0A0F1E" }}>
      <style>{JHALA_GLOBAL_CSS}</style>
      <TopStrip />
      <Header active="Home" />

      {/* HERO */}
      <section style={{ background: "#0A0F1E", color: "#F6F4EE", padding: "88px 24px 80px", position: "relative", overflow: "hidden" }}>
        <div
          style={{
            position: "absolute", inset: 0,
            backgroundImage: "linear-gradient(#23304A 1px, transparent 1px), linear-gradient(90deg, #23304A 1px, transparent 1px)",
            backgroundSize: "48px 48px", opacity: 0.25,
            maskImage: "radial-gradient(ellipse at 70% 30%, black, transparent 70%)",
          }}
        />
        <div style={{ maxWidth: 1180, margin: "0 auto", position: "relative", display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 48 }} className="hero-grid">
          <Reveal>
            <div style={{ marginBottom: 20 }}>
              <TagChip>SHOPIFY & ECOMMERCE STORE DEVELOPMENT</TagChip>
            </div>
            <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(32px, 4.6vw, 50px)", lineHeight: 1.12, margin: "0 0 20px" }}>
              Market-Ready Shopify Stores Built to Rank, Convert and Scale
            </h1>
            <p style={{ fontSize: 16.5, color: "#B8BFCF", maxWidth: 520, lineHeight: 1.65, marginBottom: 14 }}>
              JhalaTech designs and develops Shopify and ecommerce stores that launch ready for search and
              sales. Every store combines SEO-optimized structure, CRO-focused user journeys, responsive
              development and the integrations your business needs.
            </p>
            <p style={{ fontSize: 13, color: "#4CE0B3", marginBottom: 28 }}>
              No generic pitch. Start with a practical review of your current store or project.
            </p>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", marginBottom: 40 }}>
              <a href="#audit" style={{ background: "#FF5C39", color: "#0A0F1E", border: "none", padding: "13px 22px", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
                Get a Free Store Audit <ArrowUpRight size={16} />
              </a>
              <Link to="/work" style={{ color: "#F6F4EE", fontSize: 14, fontWeight: 600, textDecoration: "underline" }}>
                See Our Work
              </Link>
            </div>
            <ul style={{ display: "flex", flexWrap: "wrap", gap: "10px 20px", listStyle: "none", padding: 0, margin: 0 }}>
              {HERO_CAPABILITIES.map((cap) => (
                <li key={cap} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#B8BFCF" }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#4CE0B3", flexShrink: 0 }} />
                  {cap}
                </li>
              ))}
            </ul>
          </Reveal>

          {/* Text-only impact panel — real numbers pulled from actual shipped
              projects (see PROJECTS / Work.js ratings), no stock imagery. */}
          <Reveal delay={0.15}>
            <div style={{ background: "#0F1729", border: "1px solid #23304A", borderRadius: 8, overflow: "hidden", boxShadow: "0 30px 80px -20px rgba(0,0,0,0.5)", animation: "floatY 5s ease-in-out infinite" }}>
              <div style={{ display: "flex", gap: 6, padding: "12px 16px", borderBottom: "1px solid #23304A" }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#FF5C39" }} />
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#4CE0B3" }} />
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#3A4763" }} />
              </div>
              <div style={{ padding: "28px 26px 32px" }}>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12.5, color: "#4CE0B3", marginBottom: 26 }}>
                  $ jhalatech --impact --summary
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "28px 20px" }}>
                  {IMPACT_STATS.map((s) => (
                    <div key={s.label}>
                      <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 30, color: "#F6F4EE", lineHeight: 1 }}>{s.value}</div>
                      <div style={{ fontSize: 12.5, color: "#8B93A7", marginTop: 8, lineHeight: 1.45 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SUPPORTING CONVERSION BAR */}
      <div style={{ background: "#0F1729", padding: "18px 24px", textAlign: "center" }}>
        <p style={{ margin: 0, color: "#F6F4EE", fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 500 }}>
          Built for discovery. Designed for buying. Developed for growth.
        </p>
      </div>

      {/* CORE ECOMMERCE SERVICES */}
      <section id="services" style={{ padding: "88px 24px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <TagChip tone="signal">ECOMMERCE SERVICES</TagChip>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(24px, 3vw, 34px)", fontWeight: 700, margin: "14px 0 12px" }}>
              Ecommerce Services Built Around Growth
            </h2>
            <p style={{ color: "#4A5468", fontSize: 15, maxWidth: 620, lineHeight: 1.7, marginBottom: 40 }}>
              Choose the service that matches where your store is today and what it needs to achieve next.
            </p>
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }} className="core-services-grid">
            {CORE_SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.06}>
                <div style={{ border: "1px solid #DCD8CC", borderRadius: 8, padding: "26px 22px", background: "#FFFFFF", height: "100%", position: "relative" }}>
                  {s.badge && (
                    <span style={{ position: "absolute", top: 16, right: 16, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: "#FF5C39", border: "1px solid #FF5C3955", background: "#FF5C3914", padding: "2px 7px", borderRadius: 3 }}>
                      AI
                    </span>
                  )}
                  <s.icon size={22} style={{ color: "#4CE0B3", marginBottom: 14 }} />
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 600, margin: "0 0 8px" }}>{s.title}</h3>
                  <p style={{ color: "#6B7280", fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div style={{ marginTop: 36, textAlign: "center" }}>
              <a href="#audit" style={{ background: "#0A0F1E", color: "#F6F4EE", border: "none", padding: "13px 24px", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: "pointer", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
                Get a Free Store Audit <ArrowUpRight size={16} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INDUSTRIES — real showcase images */}
      <section style={{ padding: "0 24px 88px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <TagChip>INDUSTRIES WE BUILD FOR</TagChip>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(22px, 2.8vw, 30px)", fontWeight: 700, margin: "14px 0 36px" }}>
              Whatever you sell, we can build the store for it
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <IndustryMarquee items={INDUSTRY_STRIP} />
          </Reveal>
        </div>
      </section>

      {/* WORK — real project screenshots */}
      <section style={{ padding: "88px 24px", background: "#0A0F1E", color: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <TagChip>SELECTED WORK</TagChip>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(22px, 2.8vw, 30px)", fontWeight: 700, margin: "14px 0 36px" }}>
              Stores & products we've built
            </h2>
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 1, background: "#23304A" }}>
            {PROJECTS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.08}>
                <Link to={`/work?project=${p.hash}`} className="svc-card work-photo-card" style={{ display: "block", background: "#0A0F1E", height: "100%", textDecoration: "none", color: "inherit", border: "1px solid transparent" }}>
                  <div style={{ position: "relative", aspectRatio: "4 / 3", overflow: "hidden", background: "#0F1729" }}>
                    <img
                      src={p.image}
                      alt={p.name}
                      className="work-card-img"
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.5s ease" }}
                    />
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 45%, rgba(10,15,30,0.9) 100%)" }} />
                    <div style={{ position: "absolute", top: 12, left: 12, fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 600, color: "#0A0F1E", background: "#4CE0B3", padding: "3px 9px", borderRadius: 3 }}>
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  </div>
                  <div style={{ padding: "22px 22px 26px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "#4CE0B3" }}>#{p.hash}</span>
                      <ArrowUpRight size={15} style={{ color: "#8B93A7" }} />
                    </div>
                    <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 600, margin: "0 0 6px" }}>{p.name}</h3>
                    <p style={{ color: "#8B93A7", fontSize: 12.5, margin: "0 0 10px" }}>{p.cat}</p>
                    <p style={{ color: "#B8BFCF", fontSize: 13, lineHeight: 1.5, margin: 0 }}>{p.result}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MARKET-READY FOUNDATIONS */}
      <section id="what-we-optimize" style={{ padding: "88px 24px", background: "#FFFFFF" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <TagChip>READY FOR SEARCH AND SALES</TagChip>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(24px, 3vw, 34px)", fontWeight: 700, margin: "14px 0 36px" }}>
              What Makes a JhalaTech Store Market-Ready
            </h2>
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 18 }}>
            {FOUNDATIONS.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.06}>
                <div style={{ padding: "20px 4px" }}>
                  <f.icon size={20} style={{ color: "#FF5C39", marginBottom: 12 }} />
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 14.5, fontWeight: 600, margin: "0 0 6px" }}>{f.title}</h3>
                  <p style={{ color: "#6B7280", fontSize: 13, lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI AUTOMATION */}
      <section style={{ padding: "80px 24px", background: "#0A0F1E", color: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 48 }} className="ai-grid">
          <Reveal>
            <TagChip>CUSTOM ECOMMERCE AUTOMATION</TagChip>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 700, margin: "14px 0 14px" }}>
              Automate Repetitive Ecommerce Work With Custom AI
            </h2>
            <p style={{ color: "#B8BFCF", fontSize: 15, lineHeight: 1.7, maxWidth: 460 }}>
              We design custom AI automations around the way your ecommerce business already works.
              The solution is planned around your tools, team, rules and approval process instead of
              forcing a generic workflow.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div style={{ background: "#0F1729", border: "1px solid #23304A", borderRadius: 8, padding: "8px 22px" }}>
              {AI_STEPS.map((s, i) => (
                <div key={s.step} style={{ display: "flex", gap: 16, alignItems: "flex-start", padding: "18px 0", borderBottom: i < AI_STEPS.length - 1 ? "1px solid #23304A" : "none" }}>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 15, color: "#4CE0B3", flexShrink: 0 }}>{s.step}</span>
                  <span style={{ fontSize: 14, color: "#F6F4EE" }}>{s.label}</span>
                </div>
              ))}
              <div style={{ padding: "18px 0 8px" }}>
                <a href="#audit" style={{ background: "#4CE0B3", color: "#0A0F1E", border: "none", padding: "11px 20px", borderRadius: 4, fontWeight: 600, fontSize: 13.5, cursor: "pointer", textDecoration: "none", display: "inline-block" }}>
                  Discuss an Ecommerce AI Automation
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SUPPORTING FULL-STACK SERVICES — intentionally lighter weight */}
      <section style={{ padding: "56px 24px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(19px, 2.2vw, 24px)", fontWeight: 700, margin: "0 0 8px" }}>
              Full-Stack Support When the Store Needs More
            </h2>
            <p style={{ color: "#6B7280", fontSize: 14, maxWidth: 580, lineHeight: 1.65, marginBottom: 24 }}>
              When an ecommerce project needs custom frontend, backend, API or deployment work, JhalaTech
              can support it with React, Next.js, Node.js, APIs and DevOps.
            </p>
          </Reveal>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {FULLSTACK_SUPPORT.map((f, i) => (
              <Reveal key={f.label} delay={i * 0.05}>
                <div style={{ display: "flex", alignItems: "center", gap: 9, border: "1px solid #DCD8CC", borderRadius: 20, padding: "8px 16px", background: "#FFFFFF" }}>
                  <f.icon size={15} style={{ color: "#4CE0B3" }} />
                  <span style={{ fontSize: 13, color: "#1A2233" }}>{f.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" style={{ padding: "88px 24px", background: "#FFFFFF" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <TagChip>FROM AUDIT TO LAUNCH</TagChip>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(24px, 3vw, 34px)", fontWeight: 700, margin: "14px 0 36px" }}>
              A Clear Path to a Market-Ready Store
            </h2>
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 0 }}>
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.07}>
                <div style={{ borderLeft: "2px solid #DCD8CC", paddingLeft: 18, paddingRight: 14 }}>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 22, color: "#FF5C39", marginBottom: 10 }}>{p.step}</div>
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15.5, fontWeight: 600, margin: "0 0 6px" }}>{p.title}</h3>
                  <p style={{ color: "#6B7280", fontSize: 13, lineHeight: 1.6, margin: 0 }}>{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div style={{ marginTop: 36 }}>
              <a href="#audit" style={{ background: "#FF5C39", color: "#0A0F1E", border: "none", padding: "13px 24px", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: "pointer", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
                Start Your Ecommerce Project <ArrowUpRight size={16} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section id="faqs" style={{ padding: "88px 24px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 780, margin: "0 auto" }}>
          <Reveal>
            <TagChip>FREQUENTLY ASKED QUESTIONS</TagChip>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(24px, 3vw, 34px)", fontWeight: 700, margin: "14px 0 30px" }}>
              Questions About Ecommerce Development
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              {FAQS.map((f, i) => (
                <FAQItem key={f.q} q={f.q} a={f.a} isOpen={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CONVERSION — AUDIT FORM */}
      <section id="audit" style={{ padding: "88px 24px", background: "#0A0F1E", color: "#F6F4EE" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: 36 }}>
              <TagChip>FREE ECOMMERCE STORE AUDIT</TagChip>
              <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(24px, 3.2vw, 34px)", fontWeight: 700, margin: "14px 0 12px" }}>
                Launch a Store Ready for Search, Sales and Scale
              </h2>
              <p style={{ color: "#B8BFCF", fontSize: 15, maxWidth: 560, margin: "0 auto", lineHeight: 1.7 }}>
                Tell us where your store is today and what you want to improve. We will review the
                requirement and recommend the clearest next step.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ background: "#F6F4EE", color: "#0A0F1E", borderRadius: 10, padding: "32px 28px" }}>
              <AuditForm />
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}